/**
 * Device Subscription Manager
 * Manages WebSocket subscriptions for real-time device updates
 *
 * Two-layer architecture:
 * Layer 1: UltronCloud -> Turing Server (Webhook, managed by SyncWebhookDevices)
 * Layer 2: Turing Server -> Web App (WebSocket, managed by ConfigureWebSocket)
 */

import { ultronTuringApi } from './ultronTuringApi';
import { logger } from '../utils/logger';

const WEBSOCKET_ENABLED_DEVICES_KEY = 'websocket-enabled-devices';

// ============================================================
// Helper Functions
// ============================================================

/**
 * Extract baseSN from a device SN (strips #N suffix)
 * Example: "UTU32A-B0C7DEFFFECFE621#1" -> "UTU32A-B0C7DEFFFECFE621"
 *
 * This is important because:
 * - WebSocket subscriptions must use baseSN (without #N)
 * - Webhook updates from UltronCloud use baseSN
 * - Virtual device endpoints (#N) are only used in frontend display
 */
export function getBaseSN(sn: string): string {
  return sn.split('#')[0];
}

// ============================================================
// Layer 1: Webhook Subscription (UltronCloud -> Turing Server)
// ============================================================

/**
 * Sync webhook devices to ensure UltronCloud pushes to Turing Server
 * Should be called when devices are imported (don't wait for hourly sync)
 */
export async function syncWebhookDevices(): Promise<void> {
  try {
    logger.log('📡 [SubscriptionManager] Syncing webhook devices...');
    const result = await ultronTuringApi.syncWebhookDevices();
    logger.log('✅ [SubscriptionManager] Webhook devices synced:', result);
  } catch (error) {
    logger.error('❌ [SubscriptionManager] Failed to sync webhook devices:', error);
    throw error;
  }
}

// ============================================================
// Layer 2: WebSocket Subscription (Turing Server -> Web App)
// ============================================================

/**
 * Get WebSocket enabled devices from localStorage (cache)
 * For cross-device sync, use getWebSocketEnabledDevicesFromFirestore
 */
export function getWebSocketEnabledDevices(): string[] {
  try {
    const stored = localStorage.getItem(WEBSOCKET_ENABLED_DEVICES_KEY);
    return stored ? JSON.parse(stored) : [];
  } catch (error) {
    logger.error('Failed to get WebSocket enabled devices from cache:', error);
    return [];
  }
}

/**
 * Get WebSocket enabled devices from Firestore
 */
export async function getWebSocketEnabledDevicesFromFirestore(): Promise<string[]> {
  try {
    const { getUserPreferences } = await import('./userPreferencesApi');
    const preferences = await getUserPreferences();
    const devices = preferences.websocketEnabledDevices || [];

    // Update local cache
    localStorage.setItem(WEBSOCKET_ENABLED_DEVICES_KEY, JSON.stringify(devices));

    return devices;
  } catch (error) {
    logger.error('Failed to get WebSocket enabled devices from Firestore:', error);
    // Fallback to local cache
    return getWebSocketEnabledDevices();
  }
}

/**
 * Save WebSocket enabled devices to Firestore and localStorage
 */
async function saveWebSocketEnabledDevices(devices: string[]): Promise<void> {
  try {
    // Save to localStorage (cache)
    localStorage.setItem(WEBSOCKET_ENABLED_DEVICES_KEY, JSON.stringify(devices));

    // Save to Firestore
    const { getUserPreferences, saveUserPreferences } = await import('./userPreferencesApi');
    const preferences = await getUserPreferences();
    const updatedPreferences = {
      ...preferences,
      websocketEnabledDevices: devices
    };
    await saveUserPreferences(updatedPreferences);

    logger.log('✅ [SubscriptionManager] WebSocket enabled devices saved');
  } catch (error) {
    logger.error('Failed to save WebSocket enabled devices:', error);
    throw error;
  }
}

/**
 * Add a device to WebSocket subscription
 * Calls ConfigureWebSocket API and saves to Firestore
 *
 * Important: Always uses baseSN (without #N suffix) because:
 * - Webhook updates from UltronCloud use baseSN
 * - WebSocket matching must use the same format
 */
export async function addWebSocketEnabledDevice(deviceSN: string): Promise<void> {
  const baseSN = getBaseSN(deviceSN); // ✅ Strip #N suffix for WebSocket subscription

  try {
    const devices = await getWebSocketEnabledDevicesFromFirestore();

    if (!devices.includes(baseSN)) {
      // Call HTTP API to subscribe with baseSN
      await ultronTuringApi.configureWebSocket('subscribe', [baseSN]);
      logger.log(`📡 [SubscriptionManager] Subscribed to ${baseSN} via API (original: ${deviceSN})`);

      // Save baseSN to Firestore
      devices.push(baseSN);
      await saveWebSocketEnabledDevices(devices);
    } else {
      logger.log(`📡 [SubscriptionManager] Device ${baseSN} already subscribed`);
    }
  } catch (error) {
    logger.error(`Failed to add WebSocket subscription for ${baseSN}:`, error);
    throw error;
  }
}

/**
 * Remove a device from WebSocket subscription
 * Calls ConfigureWebSocket API and saves to Firestore
 *
 * Important: Always uses baseSN (without #N suffix) to match subscription format
 */
export async function removeWebSocketEnabledDevice(deviceSN: string): Promise<void> {
  const baseSN = getBaseSN(deviceSN); // ✅ Strip #N suffix

  try {
    const devices = await getWebSocketEnabledDevicesFromFirestore();
    const index = devices.indexOf(baseSN);

    if (index !== -1) {
      // Call HTTP API to unsubscribe with baseSN
      await ultronTuringApi.configureWebSocket('unsubscribe', [baseSN]);
      logger.log(`📡 [SubscriptionManager] Unsubscribed from ${baseSN} via API`);

      // Save to Firestore
      devices.splice(index, 1);
      await saveWebSocketEnabledDevices(devices);
    } else {
      logger.log(`📡 [SubscriptionManager] Device ${baseSN} not in subscription list`);
    }
  } catch (error) {
    logger.error(`Failed to remove WebSocket subscription for ${baseSN}:`, error);
    throw error;
  }
}

/**
 * Check if a device has WebSocket enabled
 * Uses baseSN to check since subscriptions are stored with baseSN
 */
export function isDeviceWebSocketEnabled(deviceSN: string): boolean {
  const baseSN = getBaseSN(deviceSN); // ✅ Check using baseSN
  const devices = getWebSocketEnabledDevices();
  return devices.includes(baseSN);
}

/**
 * Sync all WebSocket subscriptions on app startup
 * 
 * NOTE: ConfigureWebSocket tool doesn't exist on Turing server.
 * Layer 1 (webhook subscriptions via SubscribeDevices) is the source of truth.
 * WebSocket broadcasts all webhook updates to connected clients.
 * 
 * This function now only loads preferences from Firestore to local cache.
 */
export async function syncWebSocketSubscriptions(): Promise<void> {
  try {
    // Load from Firestore to update local cache
    const devices = await getWebSocketEnabledDevicesFromFirestore();
    
    if (devices.length > 0) {
      logger.log(`📡 [SubscriptionManager] Loaded ${devices.length} WebSocket preferences from Firestore (cache updated)`);
    } else {
      logger.log('📡 [SubscriptionManager] No WebSocket preferences in Firestore');
    }
    
    // Note: Actual webhook subscriptions are managed by syncAllImportedDevicesToWebhook (Layer 1)
    // WebSocket connection receives updates for all Layer 1 subscribed devices
  } catch (error) {
    logger.error('Failed to load WebSocket preferences:', error);
    // Don't throw - this is called on app startup, don't break the app
  }
}

/**
 * Cleanup webhook subscriptions - remove devices no longer imported
 * Layer 1: UltronCloud → Turing Server
 * Called on app startup to sync webhook subscriptions with imported devices
 */
export async function cleanupWebhookSubscriptions(): Promise<void> {
  try {
    logger.log('🧹 [Webhook Cleanup] Starting webhook subscription cleanup...');

    // Step 1: Get all currently subscribed devices from Layer 1
    const subscriptionResult = await ultronTuringApi.querySubscriptions();
    const subscribedSNs = (subscriptionResult.devices as string[]) || [];
    logger.log(`📡 [Webhook Cleanup] Currently subscribed: ${subscribedSNs.length} devices`);

    // Step 2: Get all imported devices from Firestore (intelliSpace only - legacy boards removed)
    const { getImportedDevices } = await import('./userPreferencesApi');

    const importedSNs = new Set<string>();
    const boardDevices = await getImportedDevices('intelliSpace');
    // ✅ 使用 baseSN 格式（移除 #N 後綴），與 subscribedSNs 格式一致
    // ✅ Fix: Use fallback pattern since migration may have removed 'sn' field
    boardDevices.forEach(device => {
      const deviceSN = (device as any).deviceSN || device.sn || (device as any).deviceId;
      if (deviceSN) {
        importedSNs.add(getBaseSN(deviceSN));
      }
    });
    logger.log(`📦 [Webhook Cleanup] Total imported devices: ${importedSNs.size}`);

    // Step 3: Find devices subscribed but not imported
    const devicesToUnsubscribe = subscribedSNs.filter((sn: string) => !importedSNs.has(sn));

    // Step 4: Unsubscribe orphaned devices
    if (devicesToUnsubscribe.length > 0) {
      logger.log(`🧹 [Webhook Cleanup] Unsubscribing ${devicesToUnsubscribe.length} orphaned devices:`, devicesToUnsubscribe);
      await ultronTuringApi.unsubscribeDevices(devicesToUnsubscribe);
      logger.log(`✅ [Webhook Cleanup] Unsubscribed ${devicesToUnsubscribe.length} devices`);
    } else {
      logger.log('✅ [Webhook Cleanup] No orphaned subscriptions found');
    }
  } catch (error) {
    logger.error('❌ [Webhook Cleanup] Failed to cleanup webhook subscriptions:', error);
    // Don't throw - this shouldn't break app startup
  }
}